import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type HotelSettingsUsersSubscriptionAccessController from 'lumen/controllers/hotel/settings/users/subscription-access';
import type Userhotelrelationship from 'lumen/models/userhotelrelationship';
import type { Transition } from 'lumen/services/transition';

const SORT_KEY_MAP: Record<string, string> = {
  createdAt: 'date',
  subscriptionName: 'name',
};

export interface RelationshipsPage {
  relationships: Userhotelrelationship[];
  totalResults: number;
}

export interface LoadRelationshipsParams {
  page: number;
  searchQuery?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  userId: string;
}

export interface SubscriptionAccessModel {
  loadRelationshipsTask: typeof HotelSettingsUsersSubscriptionAccessRoute.prototype.loadRelationshipsTask;
}

export default class HotelSettingsUsersSubscriptionAccessRoute extends Route {
  queryParams = {
    userId: { refreshModel: true },
    userEmail: { refreshModel: false },
  };

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  loadRelationshipsTask = task(
    { restartable: true },
    async (
      params: LoadRelationshipsParams,
    ): Promise<RelationshipsPage | undefined> => {
      try {
        const query: Record<string, string | number> = {
          user_id: params.userId,
          page: params.page,
        };

        if (params.searchQuery) {
          query['q'] = params.searchQuery;
        }

        if (params.sortBy) {
          query['sort_by'] = SORT_KEY_MAP[params.sortBy] ?? params.sortBy;
          query['sort_order'] = params.sortDirection ?? 'asc';
        }

        if (params.page === 0) {
          this.store.unloadAll('userhotelrelationship');
        }

        const results = await this.store.query<Userhotelrelationship>(
          'userhotelrelationship',
          query,
        );
        const meta = results.meta as { results_count: number };

        return {
          relationships: results.slice(),
          totalResults: meta.results_count,
        };
      } catch (error) {
        this.errorTracking.captureException(error);
        await this.alerting.showErrorAlert();
      }
    },
  );

  beforeModel(transition: Transition): void {
    const userId = transition.to?.queryParams?.['userId'];

    if (!userId) {
      this.router.transitionTo('hotel.settings.users');
    }
  }

  // Returns the load task instead of awaiting it, so the modal renders
  // immediately with a loading state rather than blocking the transition on
  // the slow legacy endpoint.
  model(): SubscriptionAccessModel {
    return { loadRelationshipsTask: this.loadRelationshipsTask };
  }

  setupController(
    controller: HotelSettingsUsersSubscriptionAccessController,
    model: SubscriptionAccessModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    void controller.resetAndLoad();
  }
}
